import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircleSpinnerOverlay } from "react-spinner-overlay";
import ICVActivityFilters from "./ICVActivityFilters";
import { ICVActivityFilterType } from "../../../models/ICVActivitiesModels";
import { ASSIGN_QC_ACTIONS, DEFAULT_OPTION, ICV_STATUS, PAGINATION_OPTIONS } from "../../../utils/Constant";
import { getICVDataAPICall } from "../../../api/icvActivityAPI";
import { dateConverterWithoutAddingDays } from "../../../utils/Util";
import ICVActivityTable from "../../../components/ICVActivityTable";
import ICVFormDetails from "../icvActivities/ICVFormDetails";
import { Button, Col } from "reactstrap";
import ICVPagination from "../../../components/ICVPagination";
import { handleFormSubmittedSlice, handleICVActivitiesPaginationDataSlice } from "../../../redux/slices/globalSlice";
import CPagination from "../../../components/CPagination";
import ModalComponent from "../../../components/ModalComponent";
import ICVAssignQC from "./ICVAssignQC";
import { LoggedUserType } from "../../../models/GlobalModels";
import { loginSelector } from "../../../redux/slices/loginSlice";
import { ROLE_IDS } from "../../../utils/RBAC";
import { clearSelectedICVActivities, handleAssignQCSlice, handleReassignQCSlice, handleGetIcvDataSlice } from "../../../redux/slices/icvActivitySlice";
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import moment from "moment";

const ICVQCActivities: React.FC = () => {
  const dispatch: any = useDispatch();
  const user: LoggedUserType = useSelector(loginSelector).user;
  const { isLoading, selectedICVActivities } = useSelector((state: any) => state.icvActivity);
  const { storeICVActivitiesPaginationData, isFormSubmitted } = useSelector((state: any) => state.global);
  const { limit, offset, selectedLimit } = storeICVActivitiesPaginationData;
  const { storeICVData } = useSelector((state: any) => state.icvActivity);
  const updatePageStore = useSelector((state: any) => state.global);
  const [formData, setFormData] = useState<any[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<ICVActivityFilterType>(() => {
    const initialQcName = user?.roleId === ROLE_IDS.QUALITY_CHECKER 
      ? { label: user?.employeeName, value: user?.userId, email: user?.emailId }
      : DEFAULT_OPTION;
    
    return {
      state: DEFAULT_OPTION,
      district: DEFAULT_OPTION,
      mandal: DEFAULT_OPTION,
      cropVillage: [],
      season: DEFAULT_OPTION,
      cropId: [],
      year: DEFAULT_OPTION,
      fromDate: moment(new Date)?.subtract('1', 'months')?.toDate(),
      patwarName: DEFAULT_OPTION,
      toDate: undefined,
      surveyStatus: DEFAULT_OPTION,
      gramPanchayat: [],
      qcName: initialQcName,
      kmlId: '',
      surveyorMobileNumber: ''
    };
  });
  const [filterData, setFilterData] = useState<any[]>([]);
  const [icvData, setIcvData] = useState(storeICVData);
  const [openAssignQcModal, setOpenAssignQcModal] = useState<boolean>(false);
  const [openReassignQcModal, setOpenReassignQcModal] = useState<boolean>(false);

  const assignQCFormRef = useRef<any>(null);
  const reassignQCFormRef = useRef<any>(null);
  const hasMadeInitialCall = useRef(false);
  const [assignFormValid, setAssignFormValid] = useState(false);
  const [reassignFormValid, setReassignFormValid] = useState(false);
  const [allowedStatusId, setAllowedStatusId] = useState(ICV_STATUS.QC_PENDING);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const handleApplyFilters = (filters: ICVActivityFilterType, shouldCallAPI: boolean = true) => {
    setFormData([]);
    setSelectedFilter(filters); // Update selectedFilter state
    
    // If shouldCallAPI is false (reset case), clear data and don't call API
    if (!shouldCallAPI) {
      // Clear the data by dispatching an empty data slice
      dispatch(handleGetIcvDataSlice({ data: [], count: 0 }));
      return;
    }
    // Only reset pagination when filters change, don't call API automatically
    if(storeICVActivitiesPaginationData.offset > 0) {
      dispatch(
        handleICVActivitiesPaginationDataSlice({
          ...storeICVActivitiesPaginationData,
          offset: 0,
        })
      );
      
      return;
    }
    
    // For submit case, always call API with the new filters directly
    getICVDataWithFilters(filters);
  };

  useEffect(() => {        
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
  }, [
    selectedFilter,
  ]);

  useEffect(() => {
    dispatch(clearSelectedICVActivities())
    if(selectedFilter?.qcName && selectedFilter.qcName.value !== DEFAULT_OPTION.value){
      setAllowedStatusId(ICV_STATUS.QC_IN_PROGRESS);
    }
    else{ 
      setAllowedStatusId(ICV_STATUS.QC_PENDING);
    }
  },[selectedFilter.qcName])

  useEffect(() => {
    if(!updatePageStore?.updatePageStore && isFormSubmitted) {
        getICVData();
        dispatch(handleFormSubmittedSlice(false));
    } 
  },[
    updatePageStore?.updatePageStore,
    isFormSubmitted
  ])

  useEffect(() => {
    // Skip on initial load or if we haven't made the initial call yet
    if (isInitialLoad || !hasMadeInitialCall.current) return;
    getICVData();
  },[
    storeICVActivitiesPaginationData.selectedLimit,
    storeICVActivitiesPaginationData.offset,
  ]);

  useEffect(() => {
    const hasExistingData = storeICVData?.data && storeICVData.data.length > 0;
    if (isInitialLoad && !hasMadeInitialCall.current) {
      hasMadeInitialCall.current = true;
      setIsInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      setIsInitialLoad(true);
      hasMadeInitialCall.current = false;
    };
  }, []);

  const getICVData = () => {
    getICVDataWithFilters(selectedFilter);
  }

  const getICVDataWithFilters = (filters: ICVActivityFilterType) => {
    dispatch(
      getICVDataAPICall(
        limit,
        offset,
        filters?.state?.value,
        filters?.district?.value,
        filters?.mandal?.value,
        filters?.cropVillage,
        filters?.season?.value,
        filters?.cropId,
        filters?.year?.value,
        filters?.fromDate
          ? dateConverterWithoutAddingDays(filters?.fromDate?.toString(), "fromDate")
          : undefined,
        filters?.toDate
          ? dateConverterWithoutAddingDays(filters?.toDate?.toString(), "toDate")
          : undefined,
        filters?.surveyStatus?.value,
        filters?.gramPanchayat,
        filters?.surveyorMobileNumber,
        filters?.qcName,
        user?.emailId,
        user?.roleId,
        filters?.kmlId
      )
    );
  }

  // Slice the data based on the pagination offset and limit
  const paginatedData = useMemo(() => {
    setIcvData(storeICVData)
    let filterData = storeICVData?.data
    if (filterData) {
      setFilterData(filterData);
      return filterData;
    }
    return [];
  }, [storeICVData, storeICVActivitiesPaginationData, selectedFilter]);

  const viewForm = (storeICVData: any) => {
    const userObject = {
      ...storeICVData,
    };
    setFormData(userObject);
  };

  const changePaginationLimit = (value: number) => {
    if (icvData?.data?.length > 0) {
      dispatch(
        handleICVActivitiesPaginationDataSlice({
          ...storeICVActivitiesPaginationData,
          offset: 0,
          limit: value,
          selectedLimit: value,
        })
      );
    }
  };

  const prevPage = () => {
    const newOffset = storeICVActivitiesPaginationData.offset - storeICVActivitiesPaginationData.selectedLimit;
    if (newOffset >= 0) {
      const newLimit = storeICVActivitiesPaginationData.selectedLimit;

      dispatch(
        handleICVActivitiesPaginationDataSlice({
          ...storeICVActivitiesPaginationData,
          offset: newOffset,
          limit: newLimit,
        })
      );
    }
  };

  const nextPage = () => {
    const count = storeICVActivitiesPaginationData.selectedLimit;
    if (icvData.count > 0 && icvData.count === count) {
      dispatch(
        handleICVActivitiesPaginationDataSlice({
          ...storeICVActivitiesPaginationData,
          offset: storeICVActivitiesPaginationData.offset + storeICVActivitiesPaginationData.selectedLimit,
          limit: storeICVActivitiesPaginationData.selectedLimit,
        })
      );
    }
  };

  const toggleAssignQCModal = () => {
    setOpenAssignQcModal(!openAssignQcModal);
  }

  
  const toggleReassignQCModal = () => {
    setOpenReassignQcModal(!openReassignQcModal);
  }

  const handleAssignSubmit = () => {
    if (assignQCFormRef.current) {
      assignQCFormRef.current.submitForm();
    }
  };

  const handleReassignSubmit = () => {
    if (reassignQCFormRef.current) {
      reassignQCFormRef.current.submitForm();
    }
  };

  const handleAssignSuccess = () => {
    setOpenAssignQcModal(false);
    dispatch(clearSelectedICVActivities());
    dispatch(handleReassignQCSlice({}));
    dispatch(handleAssignQCSlice({}));
    getICVData();
  };

  const handleReassignSuccess = () => {
    setOpenReassignQcModal(false);
    dispatch(clearSelectedICVActivities());
    dispatch(handleReassignQCSlice({}));
    dispatch(handleAssignQCSlice({}));
    getICVData();
  };

  // Get selected ICV IDs for API calls
  const selectedICVIds = selectedICVActivities?.map((record: any) => record.icvId) || [];

  return (
    <>
      {isLoading &&
        <CircleSpinnerOverlay
          loading={isLoading}
          overlayColor="rgba(147, 166, 199, 0.1)"
          color="#5C79AA"
          zIndex={9999}
        />
      }

      {updatePageStore.updatePageStore ? (
        <ICVFormDetails storeICVFormData={formData} showQCDetails={true} />
      ) : (
        <>
          <div>
            {user?.permissions?.includes('Assign-And-Reassign-QC') && 
              (selectedICVActivities?.length > 0 &&
                <div className="d-flex justify-content-end mb-2">
                  {allowedStatusId === ICV_STATUS.QC_PENDING &&
                  <Button
                    variant="contained"
                    className="mx-2 cl-btn-primary"
                    onClick={() => toggleAssignQCModal()}
                    disabled={!(selectedICVActivities?.length > 0 && allowedStatusId === ICV_STATUS.QC_PENDING)}
                  >
                    Assign To QC
                    <AssignmentIndIcon className="ms-2"/>
                  </Button>
                  }
                  {allowedStatusId === ICV_STATUS.QC_IN_PROGRESS &&
                  <Button
                    variant="contained"
                    className="mx-2 cl-btn-outline-primary"
                    onClick={() => toggleReassignQCModal()}
                    disabled={!(selectedICVActivities?.length > 0 && allowedStatusId === ICV_STATUS.QC_IN_PROGRESS)}
                  >
                    Re-assign QC
                    <NoteAltIcon className="ms-2"/>
                  </Button>
                  }
                </div>
              )
            }
            <ICVActivityFilters 
              filter={selectedFilter}
              onApplyFilters={handleApplyFilters} 
            />
            <div
              style={{ marginTop: 20, overflowX: "hidden", maxWidth: "100%" }}
            >
              {paginatedData.length > 0 ? (
                <ICVActivityTable
                  viewForm={viewForm}
                  ICVDashboardData={paginatedData}
                  allowedStatusId={allowedStatusId}
                />
              ) : (
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '60px 20px',
                    margin: '20px 0'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Please apply appropriate filters and submit to view the surveys
                    </div>
                  </div>
                </div>
              )}
            </div>

            <CPagination
              storeCHMDashboardData={icvData}
              storeCHMPaginationData={storeICVActivitiesPaginationData}
              paginationLimitOptions={PAGINATION_OPTIONS.ICV_ACTIVITIES}
              changePaginationLimit={changePaginationLimit}
              nextPage={nextPage}
              prevPage={prevPage}
              showTotalCount={false}
              showHelper={true}
              helperText={`Use the 'Next(≫)' and 'Previous(≪)' buttons to browse through more data.`}
            />
          </div>

          {openAssignQcModal &&
            <ModalComponent
              openModal={openAssignQcModal}
              toggleModal={toggleAssignQCModal}
              title={'Assign to QC'}
              children={
                <ICVAssignQC 
                  ref={assignQCFormRef} 
                  action={ASSIGN_QC_ACTIONS.ASSIGN} 
                  selectedICVActivities={selectedICVActivities} 
                  setIsFormValid={setAssignFormValid} 
                  onSuccess={handleAssignSuccess}
                />}
              footer={
                <div className="d-flex justify-content-end">
                  <Button
                    variant="contained"
                    className="mx-2 cl-btn-primary"
                    disabled={!assignFormValid}
                    onClick={handleAssignSubmit}
                  >
                    Submit
                  </Button>
                </div>
              }
            />
          }
            
          {openReassignQcModal && 
            <ModalComponent
              openModal={openReassignQcModal}
              toggleModal={toggleReassignQCModal}
              title={'Re-assign to QC'}
              children={
                <ICVAssignQC 
                  ref={reassignQCFormRef} 
                  action={ASSIGN_QC_ACTIONS.REASSIGN} 
                  selectedICVActivities={selectedICVActivities} 
                  setIsFormValid={setReassignFormValid} 
                  onSuccess={handleReassignSuccess}
                />}
              footer={
                <div className="d-flex justify-content-end">
                  <Button
                    variant="contained"
                    className="mx-2 cl-btn-primary"
                    disabled={!reassignFormValid}
                    onClick={handleReassignSubmit}
                  >
                    Submit
                  </Button>
                </div>
              }
            />
          }
        </>
      )}
    </>
  );
};

export default ICVQCActivities;
